import { updateProject } from "@/components/common/project-provider/update-project";
import { validateElementName } from "@/components/ui/tree/tree-context-menu/actions/edit-element-action";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch } from "@/store/store-hooks";
import { ActionTextField, NoTranslate } from "@faro-lotv/flat-ui";
import { IElement } from "@faro-lotv/ielement-types";
import {
  createMutationSetElementName,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, type JSX } from "react";

type EditElementNameInputProps = {
  /** The element to edit the name for */
  element: IElement;

  /** Whether to use dark mode styling */
  dark?: boolean;

  /** Whether the input should take the full width of its container */
  fullWidth?: boolean;

  /** Whether to disable gutters (padding) */
  disableGutters?: boolean;

  /** Callback after the name was changed successfully. */
  onNameChanged?(): void;
};

/**
 * @returns An input field to edit the name of a waypoint
 */
export function EditElementNameInput({
  element,
  dark,
  fullWidth,
  disableGutters,
  onNameChanged,
}: EditElementNameInputProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const onChangeName = useCallback(
    async (newName: string) => {
      try {
        await projectApiClient.applyMutations([
          createMutationSetElementName(element.id, newName),
        ]);

        await dispatch(
          updateProject({
            projectApi: projectApiClient,
            iElementQuery: {
              ids: [element.id],
            },
          }),
        );

        onNameChanged?.();
      } catch (error) {
        handleErrorWithToast({
          title: "Could not edit name",
          error,
        });
      }
    },
    [
      dispatch,
      element.id,
      handleErrorWithToast,
      onNameChanged,
      projectApiClient,
    ],
  );

  return (
    <NoTranslate>
      <ActionTextField
        inputText={element.name}
        onConfirmButtonClick={onChangeName}
        validate={validateElementName}
        disableGutters={disableGutters}
        fullWidth={fullWidth}
        dark={dark}
      />
    </NoTranslate>
  );
}
